import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { Unsubscribe } from 'router5/dist/types/base';
import { RouteParams, Routes } from '../../../../../routes-table';
import { IScheduledZoomMeetingDetailsDTO, IZoomDAO } from '../../../../../api/zoom.dao';
import { IRouter } from '../../../../../infrastructure/router';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IDateTimeFormatter } from '../../../../../infrastructure/date-formatter';
import { RADIX_PARAM } from '../../../../../constants/common.constants';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';

interface ZoomMeetingWithDateFields extends IScheduledZoomMeetingDetailsDTO {
    startTime: Date | null;
    endTime: Date | null;
    inviteLink: string;
    inviteLinkWithPassword: string;
}

class ScheduledMeetingDetailsViewModel {
    public static getParsedJoinUrl(url: string): URL | null {
        if (!url) return null;
        try {
            return new URL(url);
        } catch {
            return null;
        }
    }

    @mobx.observable
    private currentMeetingId: number;
    @mobx.observable
    private currentMeetingDetails: ZoomMeetingWithDateFields;
    @mobx.observable
    private loading: boolean;
    @mobx.observable
    private error: string | null = null;
    private subscriptions: Unsubscribe[];

    constructor(
        @IoC.Inject private readonly zoomDao: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly errorParsing: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly dateTimeFormatter: IDateTimeFormatter = IoC.Container.get(IDateTimeFormatter),
    ) {
        this.routeListeners();
    }

    public getCurrentMeetingDetails = () => {
        return this.currentMeetingDetails;
    };

    public isLoading = () => {
        return this.loading;
    };

    public getError = () => {
        return this.error;
    };

    public getCurrentMeetingId = () => {
        return this.currentMeetingId;
    };

    @mobx.action
    public setCurrentMeetingId = (value: number) => {
        this.currentMeetingId = value;
    };

    @mobx.action
    public deleteCurrentMeeting = async () => {
        try {
            this.error = null;
            await this.zoomDao.deleteScheduledMeeting(this.currentMeetingDetails.zoomId);
        } catch (e) {
            mobx.runInAction(() => {
                this.error = this.errorParsing.extractAPIError(e).errorMessage;
                alertServerError('Error', this.errorParsing.extractMessage(e));
            });
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }

        if (!this.error) {
            this.router.navigate(Routes.ZoomScheduledMeetings, {});
        }
    };

    private routeListeners() {
        this.subscriptions = [];

        this.subscriptions.push(this.router.listen(
            Routes.ZoomScheduledMeetingDetails,
            routeArgs => {
                mobx.runInAction(() => {
                    this.currentMeetingId = Number.parseInt(routeArgs.meetingId, RADIX_PARAM);
                    this.fetchScheduledMeetingDetails();
                });
            }
        ));
    }

    @mobx.action
    public fetchScheduledMeetingDetails = async () => {
        this.currentMeetingDetails = null;
        this.loading = true;

        try {
            const currentMeetingDetails = await this.zoomDao.getScheduledMeetingDetails(this.currentMeetingId);

            const joinUrl = ScheduledMeetingDetailsViewModel.getParsedJoinUrl(currentMeetingDetails.joinUrl);

            const linkPassword = joinUrl ? joinUrl.searchParams.get('pwd') : null;

            if (linkPassword) joinUrl.searchParams.delete('pwd');

            const inviteLink = linkPassword ? joinUrl.href : currentMeetingDetails.joinUrl;

            mobx.runInAction(() => {
                this.currentMeetingDetails = {
                    ...currentMeetingDetails,
                    startTime: currentMeetingDetails.startTime,
                    endTime: currentMeetingDetails.endTime,
                    inviteLink: inviteLink,
                    inviteLinkWithPassword: linkPassword ? currentMeetingDetails.joinUrl : null
                };
            });
        } catch (e) {
            mobx.runInAction(() => {
                this.error = this.errorParsing.extractAPIError(e).errorMessage;
                if (e.response.status === 404) {
                    this.router.navigate(Routes.ZoomScheduledMeetings, {});
                } else {
                    throw e;
                }
            });
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    };
}

export { ScheduledMeetingDetailsViewModel, ZoomMeetingWithDateFields };
